import { useState } from 'react';
import { PortfolioItem, PortfolioImage } from '@/hooks/useProfile';
import { normalizePortfolioItem } from '@/hooks/portfolio/usePortfolioNormalizer';

interface UsePortfolioImageOperationsProps {
  item: PortfolioItem;
  onChange: (updatedItem: PortfolioItem) => void;
}

export const usePortfolioImageOperations = ({ 
  item, 
  onChange 
}: UsePortfolioImageOperationsProps) => {
  const [isAddingImage, setIsAddingImage] = useState(false);
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  
  // Enhanced URL validation function
  const isValidImageUrl = (url: string): boolean => {
    if (!url) return false;
    
    try {
      // Accept data:image URLs for previews
      if (url.startsWith('data:image/')) {
        return true;
      }
      
      // Require HTTPS in production (allow HTTP for local development)
      const isValidProtocol = url.startsWith('https://') || 
        (process.env.NODE_ENV === 'development' && url.startsWith('http://'));
      
      return isValidProtocol;
    } catch (e) {
      console.warn('Invalid image URL format:', url, e);
      return false;
    }
  };
  
  // Always operate on a normalized version of the item
  const normalizedItem = normalizePortfolioItem(item);
  
  const handleAddImages = (imageUrls: string[]) => {
    // Validate image URLs before adding
    const validUrls = imageUrls.filter(url => {
      if (!isValidImageUrl(url)) {
        console.warn("Invalid image URL format:", url);
        return false;
      }
      return true;
    });
    
    if (validUrls.length === 0) return;
    
    const images = [...(normalizedItem.images || [])];
    
    // Check if adding these images would exceed the limit
    if (images.length + validUrls.length > 20) {
      console.warn("Maximum number of images (20) would be exceeded");
      return;
    }
    
    const newImages: PortfolioImage[] = validUrls.map(url => ({
      id: crypto.randomUUID(),
      url: url,
      order: images.length + validUrls.indexOf(url),
      caption: '',
      isLegacy: false
    }));
    
    const updatedImages = [...images, ...newImages];
    
    const updatedItem = {
      ...normalizedItem,
      images: updatedImages,
      image_url: updatedImages[0].url
    };
    
    onChange(updatedItem);
    setIsAddingImage(false);
  };
  
  const handleRemoveImage = (imageId: string) => {
    if (!normalizedItem.images) return;
    
    const updatedImages = normalizedItem.images.filter(img => img.id !== imageId);
    
    // Reorder remaining images
    updatedImages.forEach((img, idx) => {
      img.order = idx;
    });
    
    const updatedItem = {
      ...normalizedItem,
      images: updatedImages,
      image_url: updatedImages.length > 0 ? updatedImages[0].url : null
    };
    
    onChange(updatedItem);
    
    if (editingImageId === imageId) {
      setEditingImageId(null);
    }
  };
  
  const handleMoveImage = (imageId: string, direction: 'up' | 'down') => {
    if (!normalizedItem.images) return;
    
    const images = [...normalizedItem.images];
    const imageIndex = images.findIndex(img => img.id === imageId);
    
    if (imageIndex === -1) return;
    if (direction === 'up' && imageIndex === 0) return;
    if (direction === 'down' && imageIndex === images.length - 1) return;
    
    const newIndex = direction === 'up' ? imageIndex - 1 : imageIndex + 1;
    
    // Swap order values
    const temp = images[imageIndex].order;
    images[imageIndex].order = images[newIndex].order;
    images[newIndex].order = temp;
    
    // Sort by order
    images.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    const updatedItem = {
      ...normalizedItem,
      images: images,
      image_url: images[0].url
    };
    
    onChange(updatedItem);
  };
  
  const handleUpdateImageCaption = (imageId: string, caption: string) => {
    if (!normalizedItem.images) return;
    
    const updatedImages = normalizedItem.images.map(img => {
      if (img.id === imageId) {
        return { ...img, caption };
      }
      return img;
    });
    
    const updatedItem = {
      ...normalizedItem,
      images: updatedImages
    };
    
    onChange(updatedItem);
  };
  
  const handleImageUploadingChange = (isUploading: boolean) => {
    setIsImageUploading(isUploading);
  };
  
  return {
    isAddingImage,
    setIsAddingImage,
    editingImageId,
    setEditingImageId,
    isImageUploading,
    handleAddImages,
    handleRemoveImage,
    handleMoveImage,
    handleUpdateImageCaption,
    handleImageUploadingChange
  };
};
